import { useCallback, memo, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import UserLineIcon from "remixicon-react/UserLineIcon";

import { Text, InfoContainer } from "$components";
import JTable from "$components/JTable/JTable";
import useCompany from "$hooks/useCompany";
import { theme } from "$utils/theme/bataillog";
import { ACTIVITIES_LABELS } from "$utils/typeConstants";

import { useActivitiesQuery } from "$hooks/useActivitiesQuery";
import { CompanyType } from "$types";
import { usePopin } from "$components/Popin/usePopin";
import useUserForm from "$hooks/useUserForm";
import { User } from "$types/User";
import { TitleInfoContainer } from "$components/InfoContainer/TitleInfoContainer";
import ContactFormModal from "../components/ContactFormModal";
import { SimpleMuiButton } from "$components/Buttons/MuiButton";

type Props = {
  company: any;
  companyTypes: CompanyType[];
  cantEditCurrentTab: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  userList: any[];
  id: string;
};

const FullWidthInfoContainer = styled(InfoContainer)({
  width: "100%",
  marginTop: theme.spacing(2),
  "& .MuiButton-root": {
    color: "#000000 !important",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5 !important",
      color: "#000000 !important",
      boxShadow: "none !important",
    },
    "&:disabled": {
      color: "#cccccc !important",
      backgroundColor: "transparent !important",
    },
  },
});

const AddContactButton = styled(SimpleMuiButton)({
  color: "#000000 !important",
  backgroundColor: "transparent !important",
  boxShadow: "none !important",
  border: "none !important",
  transition: "background-color 0.2s ease !important",
  "&:hover": {
    backgroundColor: "#f5f5f5 !important",
    color: "#000000 !important",
    boxShadow: "none !important",
  },
  "&:disabled": {
    color: "#cccccc !important",
    backgroundColor: "transparent !important",
    pointerEvents: "none",
  },
  "&:focus": {
    backgroundColor: "#f5f5f5 !important",
    outline: "none",
  },
});

export const Contacts = memo(function Contacts({
  company,
  companyTypes,
  cantEditCurrentTab,
  isDisabled,
  isLoading,
  userList,
  id,
}: Props) {
  const { Popin, togglePopin, PopinProps } = usePopin();
  const [localUsers, setLocalUsers] = useState<any[]>(userList || []);
  const [contactPageIndex, setContactPageIndex] = useState(0);

  useEffect(() => {
    setLocalUsers(userList || []);
  }, [userList]);

  const { data: activities = [] } = useActivitiesQuery();
  const { isAgency, isCarrier, isContributor, isClient, isLogistician } =
    useCompany();
  const { user, setUser, setError, title, setTitle } = useUserForm(id);

  const handlePopin = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setUser(null);
        setError("");
      }
      togglePopin();
    },
    [setError, setUser, togglePopin]
  );

  const onAddClick = () => {
    setUser(null);
    handlePopin(true);
  };

  const onEditClick = useCallback(
    (_user: User) => {
      if (_user) {
        setUser(_user);
        handlePopin(true);
      }
    },
    [handlePopin, setUser]
  );

  const handleAddUserClick = () => {
    if (cantEditCurrentTab && company?.isActive) {
      setTitle("Ajouter un contact");
      onAddClick();
    }
  };

  const handleEditUserClick = useCallback(
    (data: any) => {
      if (cantEditCurrentTab) {
        setTitle("Édition du contact");
        onEditClick(data);
      }
    },
    [cantEditCurrentTab, onEditClick, setTitle]
  );

  const isActivityDisplayed = isAgency(company?.companyTypes);
  const isApproverDisplayed = isClient(companyTypes);

  const columns = useMemo(
    () => [
      ...(isActivityDisplayed
        ? [
            {
              id: "activity",
              Header: "activité",
              Cell: ({ row }: any) => (
                <Text>
                  {
                    ACTIVITIES_LABELS[
                      activities.find((a) => a._id === row.original.activity)
                        ?.name
                    ]
                  }
                </Text>
              ),
            },
          ]
        : []),
      {
        id: "contact",
        Header: "contact",
        Cell: ({ row }: any) => (
          <Text type="important">
            {row.original.firstname} {row.original.lastname?.toUpperCase()}
          </Text>
        ),
      },
      {
        id: "email",
        Header: "e-mail",
        Cell: ({ row }: any) => <Text>{row.original.email}</Text>,
      },
      {
        id: "phone",
        Header: "tél. mobile",
        Cell: ({ row }: any) => <Text>{row.original.phone || "N/A"}</Text>,
      },
      {
        id: "isUser",
        Header: "utilisateur",
        Cell: ({ row }: any) => (
          <Text>{row.original.isOnlyContact ? "Non" : "Oui"}</Text>
        ),
      },
      ...(isApproverDisplayed
        ? [
            {
              id: "isApprover",
              Header: "approbateur",
              Cell: ({ row }: any) => (
                <Text>{row.original.isApprover ? "Oui" : "Non"}</Text>
              ),
            },
          ]
        : []),
    ],
    [activities, isActivityDisplayed, isApproverDisplayed]
  );

  return (
    <>
      <FullWidthInfoContainer
        isToggleable
        leftHeader={
          <TitleInfoContainer title="Contact(s)" icon={<UserLineIcon />} />
        }
        rightHeader={
          <AddContactButton
            disabled={isDisabled}
            onClick={handleAddUserClick}
            className=""
            sx={
              company?.isActive === false
                ? {
                    color: "rgba(0, 0, 0, 0.37) !important",
                    pointerEvents: "none",
                  }
                : undefined
            }
            icon={<AddIcon />}
          >
            Ajouter un contact
          </AddContactButton>
        }
      >
        <JTable
          data={localUsers || []}
          columns={columns}
          pageIndex={contactPageIndex}
          setPageIndex={setContactPageIndex}
          hideCheckboxes
          hideFilters
          rowsPerPage={10}
          noDataText={`${
            isClient(companyTypes)
              ? "Ce client"
              : isContributor(companyTypes)
                ? "Cette entreprise intervenante"
                : isCarrier(companyTypes)
                  ? "Ce transporteur"
                  : isLogistician(companyTypes)
                    ? "Ce logisticien"
                    : "Cette entreprise"
          } n'a pas encore de contacts`}
          isFetching={isLoading}
          usePaper={false}
          rowProps={(row) => ({
            onClick: row.original.isDisable
              ? undefined
              : () => handleEditUserClick(row.original),
            style: {
              cursor: row.original.isDisable ? "default" : "pointer",
            },
          })}
        />
      </FullWidthInfoContainer>

      <Popin {...PopinProps}>
        <ContactFormModal
          user={user}
          title={title}
          id={id}
          company={company}
          companyTypes={companyTypes}
          togglePopin={togglePopin}
          onUserSaved={(savedUser: any) => {
            setLocalUsers((prev) => {
              const idx = prev.findIndex((u: any) => u?._id === savedUser?._id);
              if (idx === -1) {
                return [savedUser, ...prev];
              }
              const next = [...prev];
              next[idx] = { ...next[idx], ...savedUser };
              return next;
            });
          }}
        />
      </Popin>
    </>
  );
});
